/**
 * Convert API data to widget-specific data formats
 * 
 * This utility is shared across Dashboard components to ensure consistent
 * data transformation logic and reduce code duplication.
 * 
 * @param {Object} apiData - API response data with columns and rows
 * @param {Array<string>} apiData.columns - Column names from query result
 * @param {Array<Array>} apiData.rows - Row data from query result
 * @param {string} widgetType - Widget type (table, metric, pie, bar, line, etc.)
 * @param {Object} config - Widget configuration object
 * @param {string} config.chartBy - Column to use for charting
 * @param {string} config.groupBy - Column to group by
 * @param {string} config.aggregation - Aggregation function (count, sum, avg, etc.)
 * @param {string} config.displayName - Display name for metric widgets
 * @param {string} noDataLabel - Label to show when no data is available (default: 'No Data')
 * @returns {Object|Array|null} Transformed data structure or null if no valid data
 */
import { transformChartData } from './transformChartData';

export const convertToWidgetData = (apiData, widgetType, widgetConfig, noDataLabel = 'No Data') => {
    if (!apiData || !apiData.rows) {
        return null;
    }

    const { columns, rows } = apiData;

    // Extract config from dataSources (type-specific config is now inside dataSource.config)
    // Check for table or custom_query data sources
    const dataSources = widgetConfig?.dataSources || [];
    const relevantSource = dataSources.find((ds) => ds.type === 'table' || ds.type === 'custom_query');
    const config = relevantSource?.config || {};

    switch (widgetType) {
        case 'table':
        case 'table-2d':
            // Convert rows to objects with column names as keys
            return rows.map((row) => {
                const obj = {};
                columns.forEach((col, index) => {
                    obj[col] = row[index];
                });
                return obj;
            });

        case 'metric': {
            // Server returns single row with aggregated value
            // Query was: SELECT aggregation(column) as value FROM table
            if (rows.length > 0) {
                const valueIndex = columns.indexOf('value');
                if (valueIndex !== -1) {
                    const result = rows[0][valueIndex];
                    const aggregation = config.aggregation || 'count';
                    let subtitle;

                    // displayName stays at root level (widgetConfig)
                    if (widgetConfig?.displayName) {
                        subtitle = widgetConfig.displayName;
                    } else {
                        const aggregationLabel = (() => {
                            if (aggregation === 'count_distinct') return 'Distinct count';
                            if (aggregation === 'avg') return 'Average';
                            if (aggregation === 'min') return 'Minimum';
                            if (aggregation === 'max') return 'Maximum';
                            if (aggregation === 'sum') return 'Sum';
                            return 'Count';
                        })();
                        subtitle = config.chartBy
                            ? `${aggregationLabel} of ${config.chartBy}`
                            : `${aggregationLabel}`;
                    }

                    return {
                        value: typeof result === 'number' ? result.toLocaleString() : result,
                        subtitle,
                        status: 'ok',
                    };
                }
            }
            return {
                value: '0',
                subtitle: widgetConfig?.displayName || noDataLabel,
                status: 'ok',
            };
        }

        case 'pie':
        case 'donut':
        case 'funnel':
        case 'treemap': {
            // Server returns: SELECT chartBy, COUNT/SUM/etc(*) as value FROM table GROUP BY chartBy
            // columns: [chartBy, 'value']
            // rows: [[category1, count1], [category2, count2], ...]

            // Handle table-prefixed column names (e.g., config.chartBy = "hosts.host_id" but column = "host_id")
            const chartByColumn = config.chartBy?.includes('.')
                ? config.chartBy.split('.').pop()
                : config.chartBy;

            let chartByIndex = columns.indexOf(config.chartBy);
            if (chartByIndex === -1 && chartByColumn) {
                chartByIndex = columns.indexOf(chartByColumn);
            }
            const valueIndex = columns.indexOf('value');

            if (chartByIndex !== -1 && valueIndex !== -1) {
                return rows.map((row) => ({
                    name: String(row[chartByIndex]),
                    value: parseFloat(row[valueIndex]) || 0,
                }));
            }
            return null;
        }

        case 'bar':
        case 'line':
        case 'line-2d':
        case 'grouped-bar':
        case 'stacked-bar':
        case 'stacked-line':
            return transformChartData(columns, rows, widgetConfig, widgetType);

        default:
            return null;
    }
};
